/**
 * Hook React pour gérer la connexion à une caméra
 * Calqué sur useScale.ts — architecture pluggable via CameraAdapter
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import type { CameraAdapter, CameraFrame, CameraConfig } from '../services/camera';
import { CameraAdapterFactory } from '../services/camera';
import { logger } from '../utils/logger';

export interface UseCameraState {
  connected: boolean;
  connecting: boolean;
  error: string | null;
  lastFrame: CameraFrame | null;
  stream: MediaStream | null;

  connect: (config?: CameraConfig) => Promise<void>;
  disconnect: () => Promise<void>;
  captureFrame: () => Promise<CameraFrame | null>;
}

export function useCamera(): UseCameraState {
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFrame, setLastFrame] = useState<CameraFrame | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);

  const adapterRef = useRef<CameraAdapter | null>(null);

  const connect = useCallback(async (config?: CameraConfig) => {
    // Créer l'adapter si pas encore fait
    if (!adapterRef.current) {
      adapterRef.current = CameraAdapterFactory.create();
      if (!adapterRef.current) {
        setError('Caméra non supportée dans ce navigateur');
        return;
      }
    }

    setConnecting(true);
    setError(null);

    try {
      await adapterRef.current.connect(config);
      setConnected(true);
      setConnecting(false);
      setStream(adapterRef.current.getStream());
      logger.log('[useCamera] Connecté avec succès');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur connexion caméra';
      setError(errorMessage);
      setConnected(false);
      setConnecting(false);
      logger.error('[useCamera] Erreur connexion:', err);
    }
  }, []);

  const disconnect = useCallback(async () => {
    if (!adapterRef.current) return;

    try {
      await adapterRef.current.disconnect();
      setConnected(false);
      setLastFrame(null);
      setStream(null);
      setError(null);
      logger.log('[useCamera] Déconnecté');
    } catch (err) {
      logger.error('[useCamera] Erreur déconnexion:', err);
    }
  }, []);

  const captureFrame = useCallback(async (): Promise<CameraFrame | null> => {
    if (!adapterRef.current || !adapterRef.current.isConnected()) {
      logger.warn('[useCamera] captureFrame: caméra non connectée');
      return null;
    }

    try {
      const frame = await adapterRef.current.captureFrame();
      setLastFrame(frame);
      return frame;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur capture';
      setError(errorMessage);
      logger.error('[useCamera] Erreur capture:', err);
      return null;
    }
  }, []);

  // Surveiller la fin du stream vidéo (track ended) pour auto-reconnecter
  // Cela arrive si la caméra USB est débranchée ou si le navigateur coupe le stream
  const lastConfigRef = useRef<CameraConfig | undefined>();
  useEffect(() => {
    if (!stream || !connected) return;

    const tracks = stream.getVideoTracks();
    if (tracks.length === 0) return;

    const handleTrackEnded = async () => {
      logger.warn('[useCamera] Track video terminée — tentative de reconnexion');
      setConnected(false);
      setStream(null);

      // Tenter une reconnexion automatique après un court délai
      setTimeout(async () => {
        try {
          if (adapterRef.current) {
            await adapterRef.current.connect(lastConfigRef.current);
            setConnected(true);
            setStream(adapterRef.current.getStream());
            setError(null);
            logger.log('[useCamera] Reconnexion automatique réussie');
          }
        } catch (err) {
          logger.error('[useCamera] Reconnexion automatique échouée:', err);
          setError('Camera déconnectée — cliquez pour reconnecter');
        }
      }, 2000);
    };

    tracks.forEach(track => track.addEventListener('ended', handleTrackEnded));
    return () => {
      tracks.forEach(track => track.removeEventListener('ended', handleTrackEnded));
    };
  }, [stream, connected]);

  // Sauvegarder la dernière config utilisée pour la reconnexion
  const connectWithConfig = useCallback(async (config?: CameraConfig) => {
    lastConfigRef.current = config;
    await connect(config);
  }, [connect]);

  return {
    connected,
    connecting,
    error,
    lastFrame,
    stream,
    connect: connectWithConfig,
    disconnect,
    captureFrame,
  };
}
